  /* ============================================================
     Cart Drawer - rewritten for correctness and performance - v20260420
     ============================================================
     Design:
     - Single source of truth: window.CartAPI
     - Server cart fetched once on DOMContentLoaded to sync header
     - Atomic operations queue: all mutations serialized
     - Optimistic UI updates with rollback on failure
     - Bundle grouping by _bundle_id property
     - Bundles update via parallel /cart/change.js calls (per line key)
     - Proper error handling for 422 responses
     - XSS-safe attribute escaping
     - Accessible (aria-live, aria-busy, focus management)
     ============================================================ */
  (function() {
    'use strict';

    var STATE = {
      cart: null,           // last known cart object from Shopify
      pending: null,        // current in-flight Promise (serializes ops)
      lastFocused: null,    // element to restore focus to on close
      lastFetch: 0,         // timestamp of last successful cart fetch (ms)
      prevPromoCount: -1,   // tracks previous realCount for animation diffing
      prevPromoStage: undefined, // promo stage from last render (confetti on fire transition)
      promoStagesSeen: {},  // per-drawer-open dedup for JRS_PromoStageView
      optimisticAddCount: 0, // promo-eligible items added but not yet server-confirmed
      userQty: {}           // { itemKey: userRequestedQty } - overrides server qty
                            // when cart-transform clamps the parent line (bundles
                            // where server returns quantity:1 with doubled
                            // final_line_price after a /cart/change.js qty bump)
    };

    function displayQtyFor(item) {
      if (!item) return 1;
      if (Object.prototype.hasOwnProperty.call(STATE.userQty, item.key)) {
        return STATE.userQty[item.key];
      }
      return item.quantity;
    }

    function pruneUserQty() {
      if (!STATE.cart || !STATE.cart.items) { STATE.userQty = {}; return; }
      var alive = {};
      for (var i = 0; i < STATE.cart.items.length; i++) alive[STATE.cart.items[i].key] = true;
      for (var k in STATE.userQty) {
        if (Object.prototype.hasOwnProperty.call(STATE.userQty, k) && !alive[k]) {
          delete STATE.userQty[k];
        }
      }
    }

    /* ---------- Helpers ---------- */
    // 6.8.26: unified to one canonical price format sitewide (₪ prefix, no space,
    // no decimals - Daniel's call). Was already close (prefix, no space) but kept a
    // toFixed(2) fallback for non-integer amounts; the store has none, so this now
    // rounds unconditionally like every other formatter.
    // <bdi> here means every one of the ~15 call sites below is isolated by construction -
    // including the ones that place two fmt() results back to back (line price next to a
    // struck compare price), which is exactly the shape that merged into unreadable text
    // elsewhere in the theme today (see product-page-v2.css .pdp2__addon-now).
    function fmt(cents) {
      return '<bdi>₪' + (cents / 100).toFixed(2).replace(/\.00$/, '') + '</bdi>';
    }

    function escHtml(s) {
      return String(s == null ? '' : s)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
    }

    function escAttr(s) {
      // Same as escHtml but explicit for clarity at attribute call sites
      return escHtml(s);
    }

    /* ---------- Funnel analytics (Meta pixel custom events) ----------
       Every fbq call is guarded (typeof window.fbq === 'function') so a blocked
       or absent pixel (~40% in-app webviews) never throws. Values are
       gift-excluded shekels to match the drawer's displayTotal. */
    function cdCartSnapshot() {
      var c = STATE.cart, items = 0, valueCents = 0;
      if (c && c.items) {
        for (var i = 0; i < c.items.length; i++) {
          var it = c.items[i];
          if (it.properties && it.properties['_free_gift']) continue;
          items++;
          valueCents += it.final_line_price;
        }
      }
      return { items: items, value: Math.round(valueCents) / 100 };
    }

    function cdTrackDrawerOpen() {
      try {
        if (typeof window.fbq !== 'function') return;
        var s = cdCartSnapshot();
        window.fbq('trackCustom', 'JRS_DrawerOpen', { items: s.items, value: s.value });
      } catch (e) {}
    }

    // Unified funnel event: fbq (unless noFbq) + dataLayer + Clarity, each
    // independently guarded so a blocked pixel never breaks the others.
    function cdTrack(name, params, noFbq) {
      if (!noFbq) {
        try {
          if (typeof window.fbq === 'function') window.fbq('trackCustom', name, params || {});
        } catch (e) {}
      }
      try {
        window.dataLayer = window.dataLayer || [];
        var dl = { event: name };
        var p = params || {};
        for (var k in p) { if (Object.prototype.hasOwnProperty.call(p, k)) dl[k] = p[k]; }
        window.dataLayer.push(dl);
      } catch (e) {}
      try {
        if (typeof window.clarity === 'function') window.clarity('event', name);
      } catch (e) {}
    }

    // One JRS_GiftPickerView per drawer-open (reset in CartAPI.open)
    function cdTrackGiftPickerView() {
      try {
        var drawer = document.getElementById('cart-drawer');
        if (!drawer || !drawer.classList.contains('active')) return;
        if (STATE.giftPickerSeen) return;
        STATE.giftPickerSeen = true;
        cdTrack('JRS_GiftPickerView', {}, true);
      } catch (e) {}
    }

    // One JRS_PromoStageView per stage per drawer-open. Only while the drawer
    // is actually visible - background renders (header badge sync) don't count.
    function cdTrackPromoStage(stage) {
      try {
        var drawer = document.getElementById('cart-drawer');
        if (!drawer || !drawer.classList.contains('active')) return;
        STATE.promoStagesSeen = STATE.promoStagesSeen || {};
        if (STATE.promoStagesSeen[stage]) return;
        STATE.promoStagesSeen[stage] = true;
        cdTrack('JRS_PromoStageView', { stage: stage }, true);
      } catch (e) {}
    }

    // Terminal, non-concurrent write fired ONLY at checkout-click: tags the cart
    // (and therefore the resulting order) with its entry point. keepalive lets the
    // request survive the <a href="/checkout"> navigation that fires immediately
    // after. This is the ONLY new /cart writer and it never runs alongside the
    // optimistic mutation queue (the user is leaving the drawer).
    function cdSetCheckoutEntry(entry) {
      try {
        fetch('/cart/update.js', {
          method: 'POST',
          credentials: 'same-origin',
          keepalive: true,
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ attributes: { checkout_entry: entry } })
        });
      } catch (e) {}
    }

    // Selectors for fixed widgets that sit above the cart z-index (Enable a11y, sticky ATC)
    var WIDGET_SELECTORS = [
      '#INDmenu-btn', '#INDescape-btn', '#enable-induction-btn', '.enable-widget-btn',
      '#sticky-atc', '.pdp2__sticky', '#jrs-chip'
    ];
    var _hiddenWidgets = [];

    // Permanently hide Enable's ESC shortcut button (not needed, logo button handles a11y)
    function hideEnableEsc() {
      try {
        // By ID/class patterns
        document.querySelectorAll('[id*="escape"],[id*="Escape"],[class*="escape"],[class*="INDescape"],[class*="keyboard-shortcut"],[id*="keyboard"]').forEach(function(el) {
          el.style.setProperty('display', 'none', 'important');
        });
        // Leaf nodes only (no children) whose text is exactly "ESC" - avoids hiding parent containers
        document.querySelectorAll('body > *, body > * *').forEach(function(el) {
          try {
            if (el.children.length === 0 && (el.textContent || '').trim() === 'ESC') {
              el.style.setProperty('display', 'none', 'important');
            }
          } catch(e) {}
        });
      } catch(e) {}
    }

    function toggleFloatingWidgets(hide) {
      if (hide) {
        // Do NOT reset the list here, APPEND to it. A second open() while already
        // open used to wipe it and then skip every element that already carried
        // _cartHidden, so close() iterated an empty array and restored nothing -
        // the sticky ATC, the cart chip and the accessibility buttons stayed
        // display:none for the rest of the page life. There are seven independent
        // open() call sites. The dataset flag below already prevents duplicates,
        // and anything that renders between two opens still gets hidden.
        WIDGET_SELECTORS.forEach(function(sel) {
          try {
            document.querySelectorAll(sel).forEach(function(el) {
              if (!el.dataset._cartHidden) {
                el.dataset._cartHidden = '1';
                el.style.setProperty('display', 'none', 'important');
                _hiddenWidgets.push(el);
              }
            });
          } catch(e) {}
        });
        // Hide all fixed/absolute Enable elements (IND* ids) and high-z-index floaters
        try {
          document.querySelectorAll('[id^="IND"],[aria-labelledby]').forEach(function(el) {
            if (el.dataset._cartHidden) return;
            var st = window.getComputedStyle(el);
            var pos = st.position;
            if (pos === 'fixed' || pos === 'absolute') {
              el.dataset._cartHidden = '1';
              el.style.setProperty('display', 'none', 'important');
              _hiddenWidgets.push(el);
            }
          });
        } catch(e) {}
      } else {
        _hiddenWidgets.forEach(function(el) {
          if (el.dataset._cartHidden) {
            el.style.removeProperty('display');
            delete el.dataset._cartHidden;
          }
        });
        _hiddenWidgets = [];
      }
    }

    function isCartError(obj) {
      return obj && (obj.status === 422 || obj.status === 404 || typeof obj.description === 'string');
    }

    function showError(msg) {
      // Simple inline error - could become a toast later
      var body = document.getElementById('cart-drawer-body');
      if (!body) return;
      var bar = document.createElement('div');
      bar.className = 'cart-drawer__error';
      bar.setAttribute('role', 'alert');
      bar.textContent = msg || 'אירעה שגיאה. נסו שוב.';
      body.insertBefore(bar, body.firstChild);
      setTimeout(function() { if (bar.parentNode) bar.parentNode.removeChild(bar); }, 4000);
    }

    /* ---------- Network layer (serialized) ---------- */
    function request(url, opts, _retry) {
      return fetch(url, Object.assign({ credentials: 'same-origin', cache: 'no-store' }, opts || {}))
        .then(function(r) {
          if (r.status === 429) {
            if (!_retry) {
              return new Promise(function(res) { setTimeout(res, 2500); })
                .then(function() { return request(url, opts, true); });
            }
            throw new Error('יש עומס כרגע, נסה שוב עוד רגע');
          }
          return r.json().then(function(json) {
            if (!r.ok || isCartError(json)) {
              var err = new Error(json && (json.description || json.message) || ('HTTP ' + r.status));
              err.payload = json;
              throw err;
            }
            return json;
          }).catch(function(e) {
            if (e instanceof SyntaxError && !_retry) {
              return new Promise(function(res) { setTimeout(res, 800); })
                .then(function() { return request(url, opts, true); });
            }
            throw e;
          });
        });
    }

    /* ── Cart thresholds ─────────────────────────────────────────
       SOURCE OF TRUTH: theme settings (config/settings_schema.json group
       "עגלה - ספים ומבצע"), published to window.JERSEYS_CART by
       snippets/cart-config.liquid which renders in theme.liquid BEFORE this
       deferred file runs. The fallbacks below MUST equal the live setting
       values - during a CDN cache-skew window this JS can run against an
       older theme.liquid that does not publish the global yet. */
    var CART_CFG        = (typeof window !== 'undefined' && window.JERSEYS_CART) || {};
    var SHIP_THRESHOLD  = (parseInt(CART_CFG.shipThreshold, 10) || 499) * 100; // cents
    var MIN_ORDER       = (parseInt(CART_CFG.minOrder, 10) || 80) * 100;       // cents
    var PROMO_THRESHOLD = parseInt(CART_CFG.promoQty, 10) || 3;
    // Ordinal of the free item (3+1 -> רביעי, 2+1 in a time-boxed sale -> שלישי).
    var GIFT_ORD = ({ 2: 'שלישי', 3: 'רביעי', 4: 'חמישי', 5: 'שישי', 6: 'שביעי' })[PROMO_THRESHOLD] || 'נוסף';
    var PROMO_ENABLED   = CART_CFG.promoEnabled !== false;
    var _syncInProgress = false;

    function shipEligibleTotal(cart) {
      return cart.total_price;
    }

    /* ── Unified incentive bar ───────────────────────────────────
       ONE slim bar replaces the old 3+1 step widget AND the separate
       shipping bar (max one promotional element above items; combined
       tiered bar = dominant best-practice pattern). Two milestones on
       one track: gift (PROMO_THRESHOLD items) at 50%, free home
       delivery (SHIP_THRESHOLD) at 100%. Text names the NEXT unmet
       target. Honesty guards (cdPromoFired) unchanged. */
    function tplIncentiveBar(cart, promoCount, fired) {
      var truckSvg = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="11" height="11" aria-hidden="true"><path d="M1 3h15v13H1z"/><path d="M16 8h4l3 3v5h-7V8z"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/></svg>';
      var checkSvg = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3.5" width="11" height="11" aria-hidden="true"><path d="M20 6 9 17l-5-5"/></svg>';
      var giftSvg  = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="11" height="11" aria-hidden="true"><path d="M20 12v10H4V12"/><path d="M22 7H2v5h20V7z"/><path d="M12 22V7"/><path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z"/><path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z"/></svg>';

      var shipTotal = shipEligibleTotal(cart);
      var shipDone = shipTotal >= SHIP_THRESHOLD;
      var shipRemaining = Math.ceil((SHIP_THRESHOLD - shipTotal) / 100);
      var giftReached = promoCount >= PROMO_THRESHOLD;
      var giftMileDone = giftReached || fired;

      var text;
      if (PROMO_ENABLED) {
        var shipBit = shipDone
          ? '· משלוח חינם עד הבית'
          : '· עוד ₪' + shipRemaining + ' למשלוח חינם';
        if (cart.total_discount > 0 && !fired) {
          text = '<strong>הנחה פעילה בעגלה</strong> <span class="cd-bar2__muted">' + shipBit + '</span>';
        } else if (fired && shipDone) {
          text = '<strong>מעולה!</strong> מתנה בפנים + משלוח חינם עד הבית';
        } else if (fired) {
          text = '<strong>המתנה בפנים!</strong> <span class="cd-bar2__muted">' + shipBit + '</span>';
        } else if (promoCount > PROMO_THRESHOLD) {
          text = '<strong>מבצע ' + PROMO_THRESHOLD + '+1:</strong> ההנחה מחושבת בקופה';
        } else if (giftReached) {
          text = '<strong>הגעתם ל-' + PROMO_THRESHOLD + '!</strong> הפריט הבא - במתנה <span class="cd-bar2__muted">' + shipBit + '</span>';
        } else {
          var rem = PROMO_THRESHOLD - promoCount;
          text = 'עוד <strong>' + rem + ' ' + (rem === 1 ? 'פריט' : 'פריטים') + '</strong> ופריט ' + GIFT_ORD + ' במתנה' +
            ' <span class="cd-bar2__muted">' + (shipDone ? '· משלוח חינם עד הבית' : '· משלוח חינם מעל ' + fmt(SHIP_THRESHOLD)) + '</span>';
        }
      } else if (CART_CFG.flashText) {
        // Flash sale window (snippets/sale-mode.liquid): one line, discount computed at checkout.
        var flashShip = shipDone ? '· משלוח חינם עד הבית' : '· עוד ₪' + shipRemaining + ' למשלוח חינם';
        text = cart.total_discount > 0
          ? '<strong>הנחה פעילה בעגלה</strong> <span class="cd-bar2__muted">' + flashShip + '</span>'
          : '<strong>' + CART_CFG.flashText + '</strong> <span class="cd-bar2__muted">' + flashShip + '</span>';
      } else {
        text = shipDone
          ? '<strong>משלוח חינם עד הבית</strong>'
          : 'עוד <strong>₪' + shipRemaining + '</strong> למשלוח חינם עד הבית';
      }

      var pct, miles;
      if (PROMO_ENABLED) {
        pct = Math.round(50 * Math.min(promoCount / PROMO_THRESHOLD, 1) + 50 * Math.min(shipTotal / SHIP_THRESHOLD, 1));
        miles =
          '<div class="cd-mile' + (giftMileDone ? ' cd-mile--done' : '') + '" style="inset-inline-start:50%">' + (giftMileDone ? checkSvg : giftSvg) + '</div>' +
          '<div class="cd-mile' + (shipDone ? ' cd-mile--done' : '') + '" style="inset-inline-start:calc(100% - 11px)">' + (shipDone ? checkSvg : truckSvg) + '</div>';
      } else {
        pct = Math.min(100, Math.round(shipTotal / SHIP_THRESHOLD * 100));
        miles = '<div class="cd-mile' + (shipDone ? ' cd-mile--done' : '') + '" style="inset-inline-start:calc(100% - 11px)">' + (shipDone ? checkSvg : truckSvg) + '</div>';
      }

      return (
        '<div class="cd-bar2">' +
          '<div class="cd-bar2__text">' + text + '</div>' +
          '<div class="cd-track"><div class="cd-fill" style="width:' + pct + '%"></div>' + miles + '</div>' +
        '</div>'
      );
    }

    /* ── Confetti burst ────────────────────────────────────────── */
    function triggerConfetti(container) {
      var colors = ['#b8924a', '#071e44', '#e8c97a', '#ffffff', '#c4a35a', '#f0d99a'];
      var count = 34;
      for (var i = 0; i < count; i++) {
        (function(idx) {
          var el = document.createElement('div');
          el.className = 'cd-confetti-piece';
          var size = 4 + Math.random() * 5;
          el.style.cssText = [
            'left:' + (4 + Math.random() * 92) + '%',
            'width:' + size.toFixed(1) + 'px',
            'height:' + (size * 1.5).toFixed(1) + 'px',
            'background:' + colors[idx % colors.length],
            'animation-delay:' + (Math.random() * 0.55).toFixed(2) + 's',
            'animation-duration:' + (0.85 + Math.random() * 0.7).toFixed(2) + 's',
            'transform:rotate(' + Math.floor(Math.random() * 360) + 'deg)'
          ].join(';');
          container.appendChild(el);
          setTimeout(function() { if (el.parentNode) el.parentNode.removeChild(el); }, 2200);
        })(i);
      }
    }

    /* ── 3+1 Promo Progress ─────────────────────────────────────── */
    // The 3+1 is a native Shopify automatic BxGy discount ("מבצע 3+1 על כל
    // האתר!", buy 3 items -> cheapest of 4 free). It fires ONLY when a 4th
    // eligible item is in the cart. Everything here is honest to that:
    //   stage 0-2: progress copy ("עוד N פריטים...")
    //   stage 3  : threshold reached, discount NOT fired yet - action copy,
    //              the customer must ADD a 4th item to get it free
    //   stage 4  : discount actually applied by Shopify (total_discount > 0)
    // Only AUTOMATIC line discounts count as "the 3+1 fired". total_discount
    // also carries coupon codes, so a shopper who arrived on a /discount/CODE
    // link and reached 4 items was told "המתנה בפנius!", got confetti, and saw
    // "כולל מתנה" on the savings line for a gift checkout would never grant
    // (the BxGy does not combine with order discounts - deliberate policy).
    // Prefer discount_allocations[].discount_application.type; fall back to the
    // legacy discounts[].title; only then fall back to the raw sum.
    function cdPromoDiscountCents(cart) {
      if (!cart || !cart.items) return 0;
      var s = 0;
      for (var i = 0; i < cart.items.length; i++) {
        var it = cart.items[i];
        if (!it.total_discount) continue;
        var allocs = it.line_level_discount_allocations || it.discount_allocations;
        if (allocs && allocs.length) {
          for (var a = 0; a < allocs.length; a++) {
            var app = allocs[a].discount_application || {};
            if (!/3\s*\+\s*1/.test(app.title || '')) continue;
            s += (allocs[a].amount || 0);
          }
          continue;
        }
        if (it.discounts && it.discounts.length) {
          for (var d = 0; d < it.discounts.length; d++) {
            if (/3\s*\+\s*1/.test(it.discounts[d].title || '')) s += (it.discounts[d].amount || 0);
          }
          continue;
        }
        // Unidentified discounts must not be presented as a 3+1 gift.
      }
      return s;
    }

    // True only when the BxGy has really fired: enough eligible items AND
    // Shopify reports a discount. A coupon alone (discount at <4 items) or a
    // 4th item whose discount has not landed yet must NOT read as "fired".
    function cdPromoFired(promoCount, cart) {
      return promoCount >= PROMO_THRESHOLD + 1 && cdPromoDiscountCents(cart) > 0;
    }

    // Promo stage for analytics + confetti: 0..PROMO_THRESHOLD-1 = item count,
    // PROMO_THRESHOLD = threshold reached (not fired), PROMO_THRESHOLD+1 = fired.
    function cdPromoStage(promoCount, cart) {
      if (cdPromoFired(promoCount, cart)) return PROMO_THRESHOLD + 1;
      return Math.min(promoCount, PROMO_THRESHOLD);
    }

    // Merge a /cart/add.js line response into STATE.cart locally (fast path).
    // Returns false when local math would be wrong - i.e. the add crosses into
    // BxGy-fired territory where the server discounts a line - so the caller
    // falls back to a blocking /cart.js fetch. Safe to have mutated items
    // before bailing: the fallback replaces STATE.cart wholesale.
    function cdMergeAddedItem(line) {
      // Adding a line can reallocate discounts on every other line. Always
      // fetch the authoritative cart before showing the new payable amount.
      return false;
    }

    // Background truth-sync after a fast-path merge: serialized through the
    // same queue, no busy state, silent re-render when the server cart lands.
    function cdSilentReconcile() {
      enqueue(function() {
        return request('/cart.js').then(function(cart) {
          STATE.cart = cart;
          STATE.lastFetch = Date.now();
          pruneUserQty();
          render();
        }).catch(function() {});
      });
    }

    /* ── 3+1 Gift Picker ──────────────────────────────────────────
       Shown ONLY at stage 3 (threshold reached, discount not fired yet).
       Candidates come from the curated cart-gift-picks collection (seeded to
       window.__CART_GIFT_PICKS__ in theme.liquid): one-size, in stock, priced
       AT OR BELOW the cheapest promo-eligible cart item - so the picked item
       is guaranteed to be the one the BxGy frees. "בחרו את המתנה שלכם" is
       literally true. */
    function giftPickerCandidates(cartItems) {
      var picks = window.__CART_GIFT_PICKS__;
      if (!picks || !picks.length) return [];
      var cheapest = cheapestCartItem(cartItems);
      if (!cheapest) return [];
      var inCart = {};
      cartItems.forEach(function(it) { inCart[it.product_id] = true; });
      return picks.filter(function(p) {
        return p.available && !inCart[p.id] && p.price <= cheapest.price;
      }).slice(0, 6);
    }

    // One shared mini-card builder for the gift picker + upsell rail so the
    // whole strip zone speaks one visual language.
    function cdMiniCard(p, opts) {
      var name = String(p.title || '').replace('צעיף כדורגל - ', 'צעיף ');
      var priceHtml = opts.free
        ? '<s>' + fmt(p.price) + '</s> <b>חינם</b>'
        : fmt(p.price) + (p.compare_at_price > p.price ? ' <s>' + fmt(p.compare_at_price) + '</s>' : '');
      // An imageless product publishes "image": null from theme.liquid, and
      // src="" makes the browser re-request the current page URL as an image.
      // tplItem already guards this; the mini-card did not.
      var imgHtml = p.image
        ? '<img class="cd-mini__img" src="' + escAttr(p.image) + '" alt="" loading="lazy">'
        : '<span class="cd-mini__img" aria-hidden="true"></span>';
      return (
        '<div class="cd-mini" role="listitem">' +
          imgHtml +
          '<div class="cd-mini__body">' +
            '<div class="cd-mini__name" title="' + escAttr(p.title) + '">' + escHtml(name) + '</div>' +
            '<div class="cd-mini__price">' + priceHtml + '</div>' +
          '</div>' +
          '<button type="button" class="cd-mini__add" data-action="upsell-add" data-from="' + opts.from + '" data-variant-id="' + p.variant_id + '" data-product-id="' + p.id + '" aria-busy="false" aria-label="הוסיפו ' + escAttr(p.title) + '">+</button>' +
        '</div>'
      );
    }

    function tplGiftPicker(cartItems) {
      var picks = giftPickerCandidates(cartItems).slice(0, 4);
      if (!picks.length) return '';
      var cards = picks.map(function(p) { return cdMiniCard(p, { free: true, from: 'gift_picker' }); }).join('');
      return (
        '<div class="cd-strip cd-strip--gift">' +
          '<div class="cd-strip__title">בחרו את המתנה שלכם 🎁</div>' +
          '<div class="cd-strip__sub">מוסיפים אחד מאלה - והוא חינם</div>' +
          '<div class="cd-minis" role="list">' + cards + '</div>' +
        '</div>'
      );
    }

    // Serialize all cart mutations - queues if one is in flight
    function enqueue(fn) {
      var prev = STATE.pending || Promise.resolve();
      var next = prev.catch(function() {}).then(fn);
      STATE.pending = next.finally ? next.finally(function() {
        if (STATE.pending === next) STATE.pending = null;
      }) : next.then(function(v) {
        if (STATE.pending === next) STATE.pending = null;
        return v;
      }, function(e) {
        if (STATE.pending === next) STATE.pending = null;
        throw e;
      });
      return next;
    }

    /* ---------- Public API ---------- */
    var CartAPI = window.CartAPI = {
      get: function() { return STATE.cart; },

      fetch: function() {
        return enqueue(function() {
          setBusy(true);
          return request('/cart.js').then(function(cart) {
            // Auto-remove any stale _free_gift items left over from the old gift system
            var giftItems = cart.items.filter(function(it) {
              return it.properties && it.properties['_free_gift'];
            });
            if (giftItems.length === 0) {
              STATE.cart = cart;
              STATE.lastFetch = Date.now();
              render();
              return cart;
            }
            // Remove gift items one by one (best-effort - ignore per-item errors, always re-fetch)
            var p = Promise.resolve();
            giftItems.forEach(function(it) {
              p = p.then(function() {
                return request('/cart/change.js', {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ id: it.key, quantity: 0 })
                }).catch(function() {});
              });
            });
            return p.then(function() { return request('/cart.js'); }).then(function(cleanCart) {
              STATE.cart = cleanCart;
              STATE.lastFetch = Date.now();
              render();
              return cleanCart;
            });
          }).catch(function(e) {
            console.error('[cart] fetch failed:', e);
            showError('לא ניתן לטעון את העגלה');
          }).then(function(v) { setBusy(false); return v; });
        });
      },

      add: function(items, properties) {
        // items: array of {id, quantity, properties} OR single {id, quantity, properties}
        // Single bulk POST. Shopify cart-transform merges array entries into one
        // has_components parent line (mirrors backup theme behavior). Sequential
        // per-item POSTs would create separate line items = duplication. A single
        // call also avoids the 409 concurrent-mutation conflict.
        //
        // Perceived-latency design (the "3+1 response time" fix):
        // 1. Optimistic layer - for a single simple item we bump the promo/ship
        //    bars + header badge + a skeleton row BEFORE any network.
        // 2. Merge layer - the /cart/add.js response (the added line) is merged
        //    into STATE.cart locally, so one round-trip renders the final state;
        //    the old follow-up GET /cart.js becomes a SILENT background
        //    reconcile. Bundles/boxes/arrays keep the blocking two-step because
        //    cart-transform reshapes their lines server-side. Crossing into
        //    BxGy-fired territory also falls back to the blocking fetch - local
        //    math cannot apply the discount.
        var payload = Array.isArray(items) ? { items: items } : items;
        var clearPending = function() { CartAPI._addPending = false; };

        var isSingle = !Array.isArray(items) && payload && payload.id;
        var addProps = (payload && payload.properties) || {};
        var isSimpleLine = !!(isSingle && !addProps._bundle_id && !addProps._bundle_role && !addProps._box_id);
        var optimisticQty = isSimpleLine ? (parseInt(payload.quantity, 10) || 1) : 0;
        var hasPerf = !!(window.performance && performance.now);
        var t0 = hasPerf ? performance.now() : 0;

        // Marks "this shopper touched the cart during this session". Read by
        // snippets/cart-progress-chip.liquid, which must not greet a cart the
        // shopper just filled with "שמרנו לך את העגלה" - that copy is only
        // honest for a cart carried over from an earlier visit. Set on intent:
        // a rejected add costs us nothing but the (also true) ladder copy.
        try { sessionStorage.setItem('jrs_cart_touched', '1'); } catch (e) {}

        return enqueue(function() {
          if (CartAPI._addPending) return Promise.reject(new Error('add in progress'));
          CartAPI._addPending = true;
          setBusy(true);

          if (optimisticQty && STATE.cart) {
            STATE.optimisticAddCount += optimisticQty;
            render();
          }
          var clearOptimistic = function() {
            if (optimisticQty && STATE.optimisticAddCount) {
              STATE.optimisticAddCount = Math.max(0, STATE.optimisticAddCount - optimisticQty);
            }
          };

          var tPost = 0;
          return request('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload)
          }).then(function(res) {
            tPost = hasPerf ? performance.now() - t0 : 0;
            if (isSimpleLine && STATE.cart && res && res.key && cdMergeAddedItem(res)) {
              clearOptimistic();
              STATE.lastFetch = Date.now();
              render();
              cdSilentReconcile();
              if (hasPerf) cdTrack('JRS_AddLatency', { post_ms: Math.round(tPost), total_ms: Math.round(tPost), kind: 'simple' }, true);
              return STATE.cart;
            }
            return request('/cart.js').then(function(cart) {
              clearOptimistic();
              STATE.cart = cart;
              STATE.lastFetch = Date.now();
              render();
              if (hasPerf) cdTrack('JRS_AddLatency', { post_ms: Math.round(tPost), total_ms: Math.round(performance.now() - t0), kind: isSimpleLine ? 'simple_refetch' : 'bundle' }, true);
              return cart;
            });
          }).catch(function(e) {
            clearOptimistic();
            console.error('[cart] add failed:', e);
            showError(e.message || 'לא ניתן להוסיף לעגלה');
            clearPending();
            render();
            throw e;
          }).then(function(v) { clearPending(); setBusy(false); return v; },
                  function(e) { clearPending(); setBusy(false); throw e; });
        }).catch(function(e) { clearPending(); setBusy(false); throw e; });
      },

      // Change quantity by line item key (string with colon, e.g. "12345:abc")
      changeByKey: function(key, quantity) {
        // Animate the item out before removing - prevents layout-shift mis-clicks
        var animMs = 0;
        if (quantity === 0) {
          var allItems = document.querySelectorAll('.cart-item[data-line-key]');
          for (var ai = 0; ai < allItems.length; ai++) {
            if (allItems[ai].dataset.lineKey === key) {
              allItems[ai].classList.add('cart-item--removing');
              animMs = 230;
              break;
            }
          }
        }
        return enqueue(function() {
          setBusy(true);
          // Optimistic update
          if (STATE.cart) {
            var snapshot = JSON.parse(JSON.stringify(STATE.cart));
            var run = function() {
              // Resolve 1-based line index before optimistic update mutates the array
              var lineIdx = 0;
              if (STATE.cart && STATE.cart.items) {
                for (var li = 0; li < STATE.cart.items.length; li++) {
                  if (STATE.cart.items[li].key === key) { lineIdx = li + 1; break; }
                }
              }
              // optimisticUpdate uses displayQtyFor() to compute the unit price
              // ratio for cart-transform-clamped bundles. Run it BEFORE bumping
              // userQty, otherwise the ratio reads the new requested qty and
              // collapses to identity (price never updates optimistically).
              optimisticUpdate(key, quantity);
              if (quantity > 0 && Object.prototype.hasOwnProperty.call(STATE.userQty, key)) {
                STATE.userQty[key] = quantity;
              }
              render();
              var changeBody = lineIdx > 0 ? { line: lineIdx, quantity: quantity } : { id: key, quantity: quantity };
              return request('/cart/change.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(changeBody)
              }).then(function(cart) {
                STATE.cart = cart;
                var serverItem = null;
                for (var si = 0; si < cart.items.length; si++) {
                  if (cart.items[si].key === key) { serverItem = cart.items[si]; break; }
                }
                if (quantity === 0) {
                  delete STATE.userQty[key];
                } else if (serverItem && serverItem.quantity !== quantity) {
                  // Cart-transform clamped parent qty but applied price change.
                  // Override displayed qty client-side to reflect user intent.
                  STATE.userQty[key] = quantity;
                } else {
                  delete STATE.userQty[key];
                }
                render();
                return cart;
              }).catch(function(e) {
                console.error('[cart] change failed:', e);
                showError(e.message || 'לא ניתן לעדכן');
                // Resync from server - the snapshot may hold keys that a
                // discount-tier re-hash already invalidated.
                return request('/cart.js').then(function(fresh) {
                  STATE.cart = fresh;
                  render();
                  throw e;
                }, function() {
                  STATE.cart = snapshot; // offline fallback
                  render();
                  throw e;
                });
              }).then(function(v) { setBusy(false); return v; }, function(e) { setBusy(false); throw e; });
            };
            if (animMs) return new Promise(function(r) { setTimeout(r, animMs); }).then(run);
            return run();
          }
          // STATE.cart is null - nothing to change; release busy state and re-render
          // so any disabled buttons are reset via DOM rebuild.
          setBusy(false);
          render();
        });
      },

      // Change all items in a bundle (parent + addons) - sequential change calls
      changeBundleByKeys: function(keys, quantity) {
        // Animate bundle out before removing
        var animMs = 0;
        if (quantity === 0) {
          var keysStr = keys.join('|');
          var allBundles = document.querySelectorAll('.cart-item--bundle[data-bundle-keys]');
          for (var bi = 0; bi < allBundles.length; bi++) {
            if (allBundles[bi].dataset.bundleKeys === keysStr) {
              allBundles[bi].classList.add('cart-item--removing');
              animMs = 230;
              break;
            }
          }
        }
        return enqueue(function() {
          setBusy(true);
          var snapshot = STATE.cart ? JSON.parse(JSON.stringify(STATE.cart)) : null;

            var run = function() {
            // Resolve 1-based line indexes before optimistic update mutates the array
            var lineIndexMap = {};
            if (STATE.cart && STATE.cart.items) {
              for (var li = 0; li < STATE.cart.items.length; li++) {
                var itKey = STATE.cart.items[li].key;
                if (keys.indexOf(itKey) >= 0) lineIndexMap[itKey] = li + 1;
              }
            }
            // Optimistic update first (reads displayQtyFor for ratio), then bump
            // userQty so render shows the new requested qty without breaking the
            // unit-price math.
            for (var i = 0; i < keys.length; i++) {
              optimisticUpdate(keys[i], quantity);
              if (quantity > 0 && Object.prototype.hasOwnProperty.call(STATE.userQty, keys[i])) {
                STATE.userQty[keys[i]] = quantity;
              }
            }
            render();

            var p;
            if (quantity === 0) {
              // Removals: one atomic /cart/update.js with every key. Sequential
              // /cart/change.js calls break when a removal crosses a discount
              // tier (mystery box ladder): Shopify re-hashes the keys of ALL
              // remaining discounted lines, so the precomputed keys for calls
              // 2..n come back 400 "אין קוד זיהוי או פרמטר שורה תקפים".
              var updates = {};
              for (var uk = 0; uk < keys.length; uk++) updates[keys[uk]] = 0;
              p = request('/cart/update.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ updates: updates })
              });
            } else {
              // Qty bumps keep sequential /cart/change.js with the line form
              // (cart-transform quirk). Re-resolve each line index from the
              // previous response - keys/indexes can shift between calls.
              p = Promise.resolve(STATE.cart);
              keys.forEach(function(k) {
                p = p.then(function(prevCart) {
                  var idx = lineIndexMap[k];
                  if (prevCart && prevCart.items) {
                    for (var ri = 0; ri < prevCart.items.length; ri++) {
                      if (prevCart.items[ri].key === k) { idx = ri + 1; break; }
                    }
                  }
                  var changeBody = idx > 0 ? { line: idx, quantity: quantity } : { id: k, quantity: quantity };
                  return request('/cart/change.js', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(changeBody)
                  });
                });
              });
            }

            return p.then(function(cart) {
              STATE.cart = cart;
              // Mirror changeByKey: track user-requested qty for the parent line
              // when cart-transform clamps the server quantity.
              var parentKey = keys[0];
              var parentItem = null;
              for (var pi = 0; pi < cart.items.length; pi++) {
                if (cart.items[pi].key === parentKey) { parentItem = cart.items[pi]; break; }
              }
              if (quantity === 0) {
                for (var dk = 0; dk < keys.length; dk++) delete STATE.userQty[keys[dk]];
              } else if (parentItem && parentItem.quantity !== quantity) {
                STATE.userQty[parentKey] = quantity;
              } else {
                delete STATE.userQty[parentKey];
              }
              render();
              return cart;
            }).catch(function(e) {
              console.error('[cart] bundle change failed:', e);
              showError(e.message || 'לא ניתן לעדכן');
              // Resync from the server instead of rolling back to the local
              // snapshot - after a tier re-hash the snapshot holds dead keys,
              // so a rollback would make the next click fail the same way.
              return request('/cart.js').then(function(fresh) {
                STATE.cart = fresh;
                render();
                throw e;
              }, function() {
                if (snapshot) STATE.cart = snapshot;
                render();
                throw e;
              });
            }).then(function(v) { setBusy(false); return v; }, function(e) { setBusy(false); throw e; });
          };

          if (animMs) return new Promise(function(r) { setTimeout(r, animMs); }).then(run);
          return run();
        });
      },

      open: function(triggerEl) {
        STATE.lastFocused = triggerEl || document.activeElement;
        var drawer = document.getElementById('cart-drawer');
        var overlay = document.getElementById('cart-overlay');
        if (!drawer) return;
        var wasActive = drawer.classList.contains('active');
        drawer.classList.add('active');
        drawer.setAttribute('aria-hidden', 'false');
        if (overlay) {
          overlay.classList.add('active');
          overlay.setAttribute('aria-hidden', 'false');
        }
        // Scroll lock: overflow hidden is more reliable than position:fixed on iOS
        var scrollY = window.scrollY || window.pageYOffset;
        document.body.dataset.scrollY = scrollY;
        document.body.style.overflow = 'hidden';
        document.body.style.touchAction = 'none';
        // Hide floating widgets that sit above cart z-index (Enable a11y, sticky ATC)
        toggleFloatingWidgets(true);
        // Focus close button for keyboard users
        var closeBtn = document.getElementById('cart-close');
        if (closeBtn) setTimeout(function() { closeBtn.focus(); }, 50);
        // Re-fetch on open, throttled to avoid jank right after CartAPI.add()
        if (Date.now() - STATE.lastFetch > 1500) CartAPI.fetch();
        // Fire the drawer-open funnel event once per open. Skipping when the
        // drawer was already visible means ATC-while-open and re-renders (which
        // never call open()) can't double-count.
        if (!wasActive) {
          STATE.promoStagesSeen = {}; // new open = fresh JRS_PromoStageView dedup
          STATE.giftPickerSeen = false;
          cdTrackDrawerOpen();
        }
      },

      close: function() {
        var drawer = document.getElementById('cart-drawer');
        var overlay = document.getElementById('cart-overlay');
        if (!drawer) return;
        drawer.classList.remove('active');
        drawer.setAttribute('aria-hidden', 'true');
        if (overlay) {
          overlay.classList.remove('active');
          overlay.setAttribute('aria-hidden', 'true');
        }
        // Restore scroll
        document.body.style.overflow = '';
        document.body.style.touchAction = '';
        // Restore floating widgets
        toggleFloatingWidgets(false);
        if (STATE.lastFocused && STATE.lastFocused.focus) {
          try { STATE.lastFocused.focus(); } catch(e) {}
        }
      }
    };

    // Backwards-compat aliases (used by other components)
    window.openCartDrawer = function() { CartAPI.open(); };
    window.closeCartDrawer = function() { CartAPI.close(); };

    function setBusy(busy) {
      var body = document.getElementById('cart-drawer-body');
      if (body) body.setAttribute('aria-busy', busy ? 'true' : 'false');
      var drawer = document.getElementById('cart-drawer');
      if (drawer) drawer.classList.toggle('cart-drawer--loading', !!busy);
    }

    /* ---------- Optimistic update ---------- */
    function optimisticUpdate(key, quantity) {
      if (!STATE.cart || !STATE.cart.items) return;
      // Discount reallocations cannot be predicted from a single changed line.
      if (STATE.cart.total_discount > 0) return;
      var items = STATE.cart.items;
      var idx = -1;
      for (var i = 0; i < items.length; i++) {
        if (items[i].key === key) { idx = i; break; }
      }
      if (idx < 0) return;
      var item = items[idx];
      var isAddon = item.properties && item.properties._bundle_role === 'addon';
      if (quantity === 0) {
        if (!isAddon) STATE.cart.item_count -= item.quantity;
        STATE.cart.total_price -= item.final_line_price;
        items.splice(idx, 1);
        return;
      }
      // For cart-transform-clamped bundles, server keeps quantity:1 while
      // final_line_price reflects N "displayed" units. Using item.quantity as
      // the denominator inflates the ratio on each click and the total balloons
      // (₪66200 etc). Use the currently-displayed qty as the unit divisor -
      // matches what the price field actually represents.
      var displayedQty = displayQtyFor(item);
      if (!displayedQty || displayedQty < 1) displayedQty = 1;
      var oldLinePrice = item.final_line_price;
      var unitPrice = Math.round(oldLinePrice / displayedQty);
      var newLinePrice = unitPrice * quantity;
      var diff = quantity - displayedQty;
      item.quantity = quantity;
      item.final_line_price = newLinePrice;
      if (!isAddon) STATE.cart.item_count += diff;
      STATE.cart.total_price += (newLinePrice - oldLinePrice);
    }

    /* ---------- Bundle + Mystery Box grouping ---------- */
    function groupItems(items) {
      var bundles = {};
      var boxes = {};
      var ordered = [];
      var seen = {};
      var seenBox = {};

      for (var i = 0; i < items.length; i++) {
        var it = items[i];
        var props = it.properties || {};
        var bid = Object.prototype.hasOwnProperty.call(props, '_bundle_id') ? props._bundle_id : null;
        var role = props._bundle_role;
        var boxid = Object.prototype.hasOwnProperty.call(props, '_box_id') ? props._box_id : null;

        if (bid) {
          if (!bundles[bid]) bundles[bid] = { parent: null, addons: [] };
          if (role === 'parent') bundles[bid].parent = it;
          else bundles[bid].addons.push(it);
          if (!seen[bid]) {
            seen[bid] = true;
            ordered.push({ type: 'bundle', id: bid });
          }
        } else if (boxid) {
          if (!boxes[boxid]) boxes[boxid] = { items: [], name: '' };
          boxes[boxid].items.push(it);
          if (props['שם החבילה'] && !boxes[boxid].name) boxes[boxid].name = props['שם החבילה'];
          if (!seenBox[boxid]) {
            seenBox[boxid] = true;
            ordered.push({ type: 'box', id: boxid });
          }
        } else {
          ordered.push({ type: 'item', data: it });
        }
      }

      return { bundles: bundles, boxes: boxes, ordered: ordered };
    }

    /* ---------- Templates ---------- */
    var trashIcon = '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M3 6h18"/><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"/><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"/></svg>';

    // Build a single inline labels line for an item: variant size, player
    // name/number, and addon labels (no prices, no icons). Joined with mid-dot.
    // extraLabels: optional array of strings to append (used by tplBundle to
    // include addon line-item titles for legacy bundles).
    function buildPropsLine(item, extraLabels) {
      var props = (item && item.properties) || {};
      var parts = [];
      if (item && item.variant_title && item.variant_title !== 'Default Title') {
        parts.push('מידה: ' + escHtml(item.variant_title));
      }
      var nm = props['_שם'] || props['שם'];
      var num = props['_מספר'] || props['מספר'];
      if (nm)  parts.push('שם: ' + escHtml(nm));
      if (num) parts.push('מספר: ' + escHtml(num));
      for (var k in props) {
        if (!Object.prototype.hasOwnProperty.call(props, k)) continue;
        if (!props[k]) continue;
        if (k === 'שם' || k === 'מספר' || k === '_שם' || k === '_מספר') continue;
        if (k === '_free_gift' || k === '_bundle_id' || k === '_bundle_role' || k === '_addons') continue;
        if (k === '_gift_image' || k === '_gift_title' || k === 'סוג הצעיף') continue;
        var v = String(props[k] == null ? '' : props[k]);
        if (v.charAt(0) !== '₪' && v.charAt(0) !== '$') continue;
        var clean = k.charAt(0) === '_' ? k.slice(1) : k;
        if (item && item.product_title && clean === item.product_title) continue;
        parts.push(escHtml(clean));
      }
      if (extraLabels && extraLabels.length) {
        for (var ei = 0; ei < extraLabels.length; ei++) {
          if (extraLabels[ei]) parts.push(escHtml(extraLabels[ei]));
        }
      }
      if (!parts.length) return '';
      return '<div class="cart-item__props-line">' +
        parts.join(' <span class="cart-item__props-sep" aria-hidden="true">•</span> ') +
      '</div>';
    }

    function buildPropsHtml(properties, baseTitle, showPrices) {
      if (!properties) return '';
      if (showPrices === undefined) showPrices = true;
      var nameTags = '';
      var baseRow = '';
      var addonRows = '';
      // Name/number: support both old (_שם) and new (שם) key formats
      var playerName = properties['_שם'] || properties['שם'];
      var playerNumber = properties['_מספר'] || properties['מספר'];
      if (playerName) nameTags += '<span class="cart-item__prop-tag">שם: ' + escHtml(playerName) + '</span>';
      if (playerNumber) nameTags += '<span class="cart-item__prop-tag">מספר: ' + escHtml(playerNumber) + '</span>';
      if (!showPrices) {
        return nameTags ? '<div class="cart-bundle__name-tags">' + nameTags + '</div>' : '';
      }
      for (var k in properties) {
        if (!Object.prototype.hasOwnProperty.call(properties, k)) continue;
        if (k === 'שם' || k === 'מספר' || k === '_שם' || k === '_מספר') continue;
        if (!properties[k]) continue;
        var val = String(properties[k] == null ? '' : properties[k]);
        var isUnderscored = k.charAt(0) === '_';
        if (val.charAt(0) !== '₪' && val.charAt(0) !== '$') continue;
        var cleanKey = isUnderscored ? k.slice(1) : k;
        if (baseTitle && cleanKey === baseTitle) {
          // Jersey base price - no dot
          baseRow =
            '<div class="cart-item__addon cart-item__addon--base">' +
              '<span class="cart-item__addon-title">' + escHtml(cleanKey) + '</span>' +
              '<span class="cart-item__addon-price">' + escHtml(val) + '</span>' +
            '</div>';
        } else {
          addonRows +=
            '<div class="cart-item__addon">' +
              '<span class="cart-item__addon-dot" aria-hidden="true">+</span>' +
              '<span class="cart-item__addon-title">' + escHtml(cleanKey) + '</span>' +
              '<span class="cart-item__addon-price">' + escHtml(val) + '</span>' +
            '</div>';
        }
      }
      var html = '';
      if (nameTags) html += '<div class="cart-bundle__name-tags">' + nameTags + '</div>';
      if (baseRow || addonRows) html += '<div class="cart-item__addons">' + baseRow + addonRows + '</div>';
      return html;
    }

    // Compare-at price for a single addon, by its label + price (shekels).
    // SINGLE SOURCE OF TRUTH for these numbers: theme setting group
    // "מחירי השוואה לתוספות" (config/settings_schema.json), published to
    // window.JRS_ADDON_COMPARE by snippets/addon-compare-map.liquid (rendered in
    // layout/theme.liquid BEFORE this file). set-addon-compare-prices.mjs writes
    // the SAME numbers onto the addon variants' compare_at_price, and
    // sections/main-cart.liquid reads the SAME settings, so drawer, cart page,
    // PDP and admin can never diverge. The fallback below mirrors the schema
    // defaults so pricing never breaks if the global failed to load - keep it
    // equal to those defaults. Matching logic (substring order, the price===5
    // patch special-case, else identity) is unchanged and must stay so.
    var JRS_ADDON_COMPARE_FALLBACK = { "מכנסיים": 89, "גרביים": 59, "שרוול": 69, "גרסת שחקן": 79, "שדרוג": 79, "patch": 10 };
    function addonCompareShekels(name, price) {
      name = name || '';
      var map = (typeof window !== 'undefined' && window.JRS_ADDON_COMPARE) || JRS_ADDON_COMPARE_FALLBACK;
      if (name.indexOf('מכנסיים') !== -1) return map['מכנסיים'];
      if (name.indexOf('גרביים')  !== -1) return map['גרביים'];
      if (name.indexOf('שרוול')   !== -1) return map['שרוול'];
      if (name.indexOf('גרסת שחקן') !== -1 || name.indexOf('שדרוג') !== -1) return map['גרסת שחקן'];
      if (price === 5) return map['patch']; // patches
      return price; // unknown -> compare == price (no extra savings)
    }

    function buildPriceHtml(item) {
      var linePrice = item.final_line_price;
      var original = item.original_line_price;
      var compare = original > linePrice
        ? '<span class="cart-item__compare">' + fmt(original) + '</span>' : '';
      return '<div class="cart-item__price">' + fmt(linePrice) + compare + '</div>';
    }

    // SINGLE predicate for "counts toward the 3+1 promo": excludes addon
    // lines, legacy free-gift lines and Mystery Box lines (the tiered box
    // discount must not combine with the 3+1 - business rule). Used by the
    // promo count, the cheapest-item calc and the footer so they can never
    // drift apart.
    function isPromoEligible(it) {
      var p = (it && it.properties) || {};
      return !(p._bundle_role === 'addon' || p['_free_gift'] || p['_box_id']);
    }

    // Returns the cheapest promo-eligible item in cart (for 3+1 promo reveal)
    function cheapestCartItem(cartItems) {
      var eligible = cartItems.filter(isPromoEligible);
      if (!eligible.length) return null;
      return eligible.reduce(function(min, it) {
        return it.price < min.price ? it : min;
      });
    }

    // /cart.js returns FULL-size image URLs (1200px+); decode after a full
    // re-render is slow enough to flash blank thumbs. Shopify CDN resizes
    // via ?width= - request only what the 64px thumb needs (2x retina).
    function cdSizedImg(url, w) {
      if (!url) return url;
      return url + (url.indexOf('?') > -1 ? '&' : '?') + 'width=' + w;
    }

    function tplItem(item) {
      if (item.properties && item.properties['_free_gift']) return '';

      var imgSrc = item.image ? escAttr(cdSizedImg(item.image, 160)) : '';
      var url = escAttr(item.url || '#');
      var title = escHtml(item.product_title);
      var propsLine = buildPropsLine(item);
      var priceHtml = buildPriceHtml(item);
      var img = imgSrc
        ? '<img src="' + imgSrc + '" alt="' + escAttr(item.product_title) + '" loading="lazy">'
        : '<div class="cart-item__image-placeholder"></div>';

      // Real Shopify discount on this line (3+1 BxGy or coupon): gold badge
      // with the actual amount - never invented client-side.
      var lineDiscount = Math.max(0, item.original_line_price - item.final_line_price);
      var discountBadge = lineDiscount > 0
        ? '<span class="cart-item__discount-badge">חיסכון ' + fmt(lineDiscount) + '</span>'
        : '';

      var keyAttr = escAttr(item.key);
      var qty = displayQtyFor(item);
      // Cart-transform bundles (has_components) cannot be incremented via
      // /cart/change.js - Shopify multiplies the merged line price by the new
      // target qty (730 NIS × 3 = 2190 NIS exponential trap). The bundle's
      // child variants are collapsed server-side so changeBundleByKeys can't
      // reach them either. Lock qty to 1 + show remove only. Customer must
      // re-add from product page to buy a second bundle (standard pattern for
      // cart-transform bundles in Shop app, Bundles app, etc).
      var isBundle = !!item.has_components;
      var qtyControls = isBundle
        ? '<span class="cart-item__qty-value cart-item__qty-value--locked" aria-label="כמות">' + qty + '</span>'
        : '<div class="cart-item__qty">' +
            '<button type="button" class="cart-item__qty-btn" data-action="change" data-key="' + keyAttr + '" data-qty="' + (qty - 1) + '" aria-label="הפחת כמות">−</button>' +
            '<span class="cart-item__qty-value" aria-label="כמות">' + qty + '</span>' +
            '<button type="button" class="cart-item__qty-btn" data-action="change" data-key="' + keyAttr + '" data-qty="' + (qty + 1) + '" aria-label="הוסף כמות">+</button>' +
          '</div>';
      // One flex row: image | content | side column (qty over a quiet remove
      // link). No detached bottom bar - the approved-mockup layout.
      return (
        '<div class="cart-item" data-line-key="' + keyAttr + '">' +
          '<div class="cart-item__top">' +
            '<a href="' + url + '" class="cart-item__image">' + img + '</a>' +
            '<div class="cart-item__info">' +
              '<a href="' + url + '" class="cart-item__title">' + title + '</a>' +
              discountBadge + propsLine + priceHtml +
            '</div>' +
            '<div class="cart-item__side">' +
              qtyControls +
              '<button type="button" class="cart-item__rm" data-action="change" data-key="' + keyAttr + '" data-qty="0" aria-label="הסרה מהעגלה">הסרה</button>' +
            '</div>' +
          '</div>' +
        '</div>'
      );
    }

    // Per-shirt detail line for a Mystery Box row (shows non-price box props).
    function boxPropsLine(item) {
      var p = (item && item.properties) || {};
      var parts = [];
      if (p['קהל']) parts.push(escHtml(p['קהל']));
      if (p['סוג']) parts.push(escHtml(p['סוג']));
      if (p['מידה']) parts.push('מידה: ' + escHtml(p['מידה']));
      if (p['שם על החולצה']) parts.push('שם: ' + escHtml(p['שם על החולצה']));
      if (p['מספר על החולצה']) parts.push('מספר: ' + escHtml(p['מספר על החולצה']));
      if (p['קבוצות מועדפות']) parts.push(escHtml(p['קבוצות מועדפות']));
      if (p['צבעים']) parts.push(escHtml(p['צבעים']));
      if (p['הערה']) parts.push('הערה: ' + escHtml(p['הערה']));
      if (!parts.length) return '';
      return '<div class="cart-item__props-line">' +
        parts.join(' <span class="cart-item__props-sep" aria-hidden="true">•</span> ') +
      '</div>';
    }

    // Mystery Box: one card grouping all shirts sharing a _box_id. Lines stay
    // separate server-side (each shirt keeps its own properties for fulfillment);
    // this only groups them visually. Qty is fixed at 1 per shirt (remove only).
    function tplBox(box) {
      var items = box.items.slice().sort(function(a, b) {
        var ai = parseInt((a.properties && a.properties['_box_index']) || '0', 10);
        var bi = parseInt((b.properties && b.properties['_box_index']) || '0', 10);
        return ai - bi;
      });
      var count = items.length;
      var boxTotal = 0;
      for (var t = 0; t < items.length; t++) boxTotal += items[t].final_line_price;

      var first = items[0];
      var imgSrc = first && first.image ? escAttr(cdSizedImg(first.image, 160)) : '';
      var img = imgSrc
        ? '<img src="' + imgSrc + '" alt="" loading="lazy">'
        : '<div class="cart-item__image-placeholder"></div>';
      var nameHtml = box.name
        ? '<span class="cart-box__name"> • ' + escHtml(box.name) + '</span>' : '';

      var rows = items.map(function(it) {
        var keyAttr = escAttr(it.key);
        var idx = (it.properties && it.properties['_box_index']) ? it.properties['_box_index'] : '';
        return (
          '<div class="cart-box__row" data-line-key="' + keyAttr + '">' +
            '<div class="cart-box__row-info">' +
              '<div class="cart-box__row-title">חולצה ' + escHtml(idx) + '</div>' +
              boxPropsLine(it) +
            '</div>' +
            '<div class="cart-box__row-side">' +
              '<span class="cart-item__price">' + fmt(it.final_line_price) + '</span>' +
              '<button type="button" class="cart-item__remove" data-action="change" data-key="' + keyAttr + '" data-qty="0" aria-label="הסרת חולצה">' + trashIcon + '</button>' +
            '</div>' +
          '</div>'
        );
      }).join('');

      // One trash on the head removes the whole box - reuses the multi-key
      // bundle-change handler (qty 0 across every line in the group).
      var allKeysAttr = escAttr(items.map(function(it) { return it.key; }).join('|'));

      return (
        '<div class="cart-item cart-item--box">' +
          '<div class="cart-box__head">' +
            '<a class="cart-item__image" href="/products/mystery-box">' + img + '</a>' +
            '<div class="cart-box__head-info">' +
              '<div class="cart-box__title">מיסטרי בוקס (' + count + ' חולצות)' + nameHtml + '</div>' +
              '<div class="cart-item__price">' + fmt(boxTotal) + '</div>' +
            '</div>' +
            '<button type="button" class="cart-item__remove cart-box__remove-all" data-action="bundle-change" data-bundle-keys="' + allKeysAttr + '" data-qty="0" aria-label="הסרת כל הבוקס מהעגלה">' + trashIcon + '</button>' +
          '</div>' +
          '<div class="cart-box__rows">' + rows + '</div>' +
        '</div>'
      );
    }

    function tplBundle(bundle) {
      var parent = bundle.parent;
      if (!parent) {
        return bundle.addons.map(tplItem).join('');
      }

      var bundleTotal = parent.final_line_price;
      var allKeys = [parent.key];
      for (var i = 0; i < bundle.addons.length; i++) {
        bundleTotal += bundle.addons[i].final_line_price;
        allKeys.push(bundle.addons[i].key);
      }
      var keysAttr = escAttr(allKeys.join('|'));

      var bundleCompareTotal = parent.original_line_price;
      for (var ci = 0; ci < bundle.addons.length; ci++) {
        bundleCompareTotal += bundle.addons[ci].original_line_price;
      }
      var bundleSaved = bundleCompareTotal > bundleTotal ? bundleCompareTotal - bundleTotal : 0;
      var bundlePriceHtml = bundleSaved > 0
        ? '<div class="cart-item__price">' + fmt(bundleTotal) + '<span class="cart-item__compare">' + fmt(bundleCompareTotal) + '</span></div>'
        : '<div class="cart-item__price">' + fmt(bundleTotal) + '</div>';

      var imgSrc = parent.image ? escAttr(cdSizedImg(parent.image, 160)) : '';
      var url = escAttr(parent.url || '#');
      var title = escHtml(parent.product_title);

      // Inline props line: variant + name/number + addon line-item titles
      var addonTitles = [];
      for (var ai = 0; ai < bundle.addons.length; ai++) {
        addonTitles.push(bundle.addons[ai].product_title);
      }
      var propsLine = buildPropsLine(parent, addonTitles);

      var img = imgSrc
        ? '<img src="' + imgSrc + '" alt="' + escAttr(parent.product_title) + '" loading="lazy">'
        : '<div class="cart-item__image-placeholder"></div>';
      var qty = displayQtyFor(parent);

      return (
        '<div class="cart-item cart-item--bundle" data-bundle-keys="' + keysAttr + '">' +
          '<div class="cart-item__top">' +
            '<a href="' + url + '" class="cart-item__image">' + img + '</a>' +
            '<div class="cart-item__info">' +
              '<a href="' + url + '" class="cart-item__title">' + title + '</a>' +
              propsLine +
              bundlePriceHtml +
            '</div>' +
          '</div>' +
          '<div class="cart-item__bottom">' +
            '<div class="cart-item__qty">' +
              '<button type="button" class="cart-item__qty-btn" data-action="bundle-change" data-bundle-keys="' + keysAttr + '" data-qty="' + (qty - 1) + '" aria-label="הפחת כמות">−</button>' +
              '<span class="cart-item__qty-value">' + qty + '</span>' +
              '<button type="button" class="cart-item__qty-btn" data-action="bundle-change" data-bundle-keys="' + keysAttr + '" data-qty="' + (qty + 1) + '" aria-label="הוסף כמות">+</button>' +
            '</div>' +
            '<button type="button" class="cart-item__remove" data-action="bundle-change" data-bundle-keys="' + keysAttr + '" data-qty="0" aria-label="הסרה מהעגלה">' + trashIcon + '</button>' +
          '</div>' +
        '</div>'
      );
    }

    function tplSkeletonRow() {
      return '<div class="cart-item cart-item--skeleton">' +
        '<div class="cart-item__top" style="display:flex;gap:16px;">' +
          '<div class="skel" style="width:90px;height:110px;flex-shrink:0;border-radius:10px;"></div>' +
          '<div style="flex:1;display:flex;flex-direction:column;gap:8px;padding-top:4px;">' +
            '<div class="skel" style="height:14px;width:75%;border-radius:4px;"></div>' +
            '<div class="skel" style="height:12px;width:45%;border-radius:4px;"></div>' +
            '<div class="skel" style="height:16px;width:30%;border-radius:99px;margin-top:6px;"></div>' +
          '</div>' +
        '</div>' +
      '</div>';
    }

    function tplSkeleton() {
      var row = '<div class="cart-item cart-item--skeleton">' +
        '<div class="cart-item__top" style="display:flex;gap:16px;">' +
          '<div class="skel" style="width:90px;height:110px;flex-shrink:0;border-radius:10px;"></div>' +
          '<div style="flex:1;display:flex;flex-direction:column;gap:8px;padding-top:4px;">' +
            '<div class="skel" style="height:14px;width:75%;border-radius:4px;"></div>' +
            '<div class="skel" style="height:12px;width:45%;border-radius:4px;"></div>' +
            '<div class="skel" style="height:16px;width:30%;border-radius:99px;margin-top:6px;"></div>' +
          '</div>' +
        '</div>' +
      '</div>';
      return row + row;
    }

    function tplEmpty() {
      return '<div class="cart-drawer__empty">' +
        '<svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true"><circle cx="8" cy="21" r="1"/><circle cx="19" cy="21" r="1"/><path d="M2.05 2.05h2l2.66 12.42a2 2 0 0 0 2 1.58h9.78a2 2 0 0 0 1.95-1.57l1.65-7.43H5.12"/></svg>' +
        '<p>העגלה שלך ריקה</p>' +
        '<a href="/collections/all" class="cart-drawer__empty-cta">התחילו לקנות</a>' +
        '</div>';
    }

    /* ---------- In-drawer upsell horizontal carousel ----------
       Renders into #cart-drawer-rail (separate zone, above sticky footer).
       Reads window.__CART_UPSELLS__ - team-aware list maintained by
       cart-upsell-popup.js (complements promoted to front after jersey-add).
       Card layout: 56px square thumb + 1-line truncated title + price + 28px '+'.
       Spinner shown inline via aria-busy without redrawing the rail. */
    var UPSELL_MAX_CARDS = 8; // total DOM cap; the rest just scroll

    function tplUpsellRail(cartItems, railTitle) {
      var upsells = window.__CART_UPSELLS__;
      if (!upsells || !upsells.length) return '';
      var cartProductIds = {};
      cartItems.forEach(function(it) { cartProductIds[it.product_id] = true; });
      var available = upsells.filter(function(u) { return u.available && !cartProductIds[u.id]; });
      if (!available.length) return '';

      // Relevance ordering: team-complement matches lead, generic cart-upsells
      // follow. is_complement is stamped by cart-upsell-popup.js when its
      // team->complement map (scarf + ball) promotes a match after a jersey is
      // added; we reuse that single map, we do not build a new one here. A
      // stable partition keeps each group's existing order so the first 1-2
      // hero cards are the most relevant (e.g. Brazil jersey -> Brazil scarf/ball).
      var complements = [], generics = [];
      for (var pi = 0; pi < available.length; pi++) {
        if (available[pi].is_complement) complements.push(available[pi]);
        else generics.push(available[pi]);
      }
      var ordered = complements.concat(generics).slice(0, UPSELL_MAX_CARDS);

      var cards = ordered.map(function(u) {
        return cdMiniCard(u, { free: false, from: 'drawer_rail' });
      }).join('');
      return (
        '<div class="cd-strip">' +
          '<div class="cd-strip__title">' + (railTitle || 'משלימים את הסט') + '</div>' +
          '<div class="cd-minis" role="list">' + cards + '</div>' +
        '</div>'
      );
    }

    function renderUpsellRail() {
      var rail = document.getElementById('cart-drawer-rail');
      if (!rail) return;
      var cart = STATE.cart;
      var items = (cart && cart.items) || [];
      var realCount = items.reduce(function(sum, it) {
        var role = it.properties && it.properties._bundle_role;
        var isGift = it.properties && it.properties['_free_gift'];
        if (role === 'addon' || isGift) return sum;
        return sum + displayQtyFor(it);
      }, 0);
      // Hide rail entirely when cart is empty (empty-state takes over)
      if (realCount === 0) { rail.innerHTML = ''; return; }
      // Gift picker owns the drawer's product-strip real estate at stage 3 -
      // one strip only, never two competing rows.
      if (STATE.giftPickerVisible) {
        rail.innerHTML = tplGiftPicker(items);
        cdTrackGiftPickerView();
        return;
      }
      // Contextual heading: before the threshold every rail add advances the
      // 3+1 - say so; after the discount fired, pivot to set-upgrade framing.
      var railTitle = 'השלם את הסט שלך';
      if (PROMO_ENABLED && cart && !cart.total_discount) {
        var railPromoCount = items.reduce(function(sum, it) {
          return isPromoEligible(it) ? sum + displayQtyFor(it) : sum;
        }, 0);
        if (cdPromoFired(railPromoCount, cart)) railTitle = 'משדרגים את הסט';
        else if (railPromoCount < PROMO_THRESHOLD) railTitle = 'משלימים ל-' + PROMO_THRESHOLD + ' ומקבלים פריט ' + GIFT_ORD + ' במתנה';
      }
      rail.innerHTML = tplUpsellRail(items, railTitle);
    }

    var PAYMENT_LOGOS = [
      { src: 'https://cdn.shopify.com/s/files/1/0739/1415/5197/files/payment-visa_1e411364-9c03-4e55-b702-5fd0e7dcba24.png?v=1777894508',        alt: 'Visa' },
      { src: 'https://cdn.shopify.com/s/files/1/0739/1415/5197/files/payment-mastercard_39045b3a-c4d6-4179-9466-ab7ff84980ab.png?v=1777894508',  alt: 'Mastercard' },
      { src: 'https://cdn.shopify.com/s/files/1/0739/1415/5197/files/payment-applepay_0a1b73f6-e3d4-4f82-8694-bb9ccd33d6bb.png?v=1777894508',    alt: 'Apple Pay' },
      { src: 'https://cdn.shopify.com/s/files/1/0739/1415/5197/files/payment-googlepay_5daa9473-04f3-4fe5-9b54-1ebb4f86f6ad.png?v=1777894508',   alt: 'Google Pay' },
      { src: 'https://cdn.shopify.com/s/files/1/0739/1415/5197/files/payment-bit_ccd89bbd-49ac-4358-ac15-261d5e03c5e4.png?v=1777894508',         alt: 'Bit' },
      { src: 'https://cdn.shopify.com/s/files/1/0739/1415/5197/files/payment-paybox_665466a5-b4fc-431b-97b1-d73f105fcff3.png?v=1777894507',      alt: 'PayBox' },
      { src: 'https://cdn.shopify.com/s/files/1/0739/1415/5197/files/payment-bank_9178f001-24b9-4d38-a660-6f3ab0dc8efd.png?v=1777894508',        alt: 'העברה בנקאית' }
    ];

    function buildPaymentLogos() {
      return (
        '<div class="cd-payment-logos" aria-label="אמצעי תשלום">' +
          PAYMENT_LOGOS.map(function(p) {
            return '<img src="' + p.src + '" alt="' + p.alt + '" class="cd-pi" loading="lazy" decoding="async">';
          }).join('') +
        '</div>'
      );
    }

    function tplFooter(cart, displayTotal, promoCount) {
      var total = displayTotal !== undefined ? displayTotal : cart.total_price;
      // Match checkout: applied discounts only, never catalog compare-at prices.
      var totalSaved = cart.total_discount || 0;
      var fired = cdPromoFired(promoCount, cart);
      // One small green line under the total: real savings + (at stage 3)
      // the prospective 4th-item nudge, merged with a mid-dot. Nothing else
      // sits between the total and the CTA (Baymard/Nike pattern).
      var saveBits = [];
      if (totalSaved > 0) saveBits.push('הנחות שהוחלו: ' + fmt(totalSaved));
      if (PROMO_ENABLED && promoCount === PROMO_THRESHOLD && !cart.total_discount && cdPromoDiscountCents(cart) === 0) {
        var promoFree = cheapestCartItem(cart.items);
        if (promoFree) saveBits.push('הוסיפו פריט ' + GIFT_ORD + ' ותחסכו עד ' + fmt(promoFree.price) + ' נוספים');
      }
      var saveLine = saveBits.length
        ? '<div class="cart-drawer__save-line">' + saveBits.join(' · ') + '</div>'
        : '';
      // Footer hierarchy: total -> save line -> CTA (min-order gate INSIDE
      // the disabled button). Everything else lives quietly under the CTA:
      // payment logos, MTO legal (must stay always visible pre-checkout,
      // חוק הגנת הצרכן 14ג(ד)(4)), coupon accordion, secondary links.
      var checkoutBtn = total < MIN_ORDER
        ? '<button type="button" class="cart-drawer__checkout cart-drawer__checkout--disabled" disabled>עוד ' + fmt(MIN_ORDER - total) + ' למינימום הזמנה של ' + fmt(MIN_ORDER) + '</button>'
        : '<a href="/checkout?locale=he" class="cart-drawer__checkout" id="cart-checkout-btn">' +
            '<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" aria-hidden="true" style="flex-shrink:0"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>' +
            'השלמת הרכישה המאובטחת' +
          '</a>';
      return (
        '<div class="cart-drawer__footer">' +
          '<div class="cart-drawer__total-row">' +
            '<span class="cart-drawer__total-label">סה"כ לפני משלוח</span>' +
            '<span class="cart-drawer__total-price">' + fmt(total) + '</span>' +
          '</div>' +
          saveLine +
          '<div class="cart-drawer__save-line">איסוף חינם · עד הבית ' + (total >= SHIP_THRESHOLD ? 'חינם' : fmt(2900)) + '</div>' +
          '<div class="cart-drawer__actions">' + checkoutBtn + '</div>' +
          buildPaymentLogos() +
          // Legal disclosure (חוק הגנת הצרכן 14ג(ד)(4)): printed items are made to order,
          // must be shown before checkout. Keep muted; do not collapse or bury.
          '<div class="cart-drawer__mto-line">פריטים מודפסים אינם ניתנים להחלפה בשל בחירת מידה שגויה או לביטול לאחר תחילת הייצור. <a href="/pages/shipping-returns#ship-4">לתנאי הביטול</a></div>' +
          '<div class="cd-discount">' +
            '<button type="button" class="cd-discount__toggle" id="cd-coupon-toggle" aria-expanded="false" aria-controls="cd-coupon-body" onclick="(function(b){var open=b.closest(\'.cd-discount\').classList.toggle(\'open\');b.setAttribute(\'aria-expanded\',String(open))})(this)">' +
              '<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/></svg>' +
              ' יש לי קוד קופון' +
              '<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="cd-discount__chevron" aria-hidden="true"><path d="m6 9 6 6 6-6"/></svg>' +
            '</button>' +
            '<div class="cd-discount__body" id="cd-coupon-body">' +
              '<div class="cd-discount__row">' +
                '<input type="text" class="cd-discount__input" id="cd-coupon" aria-labelledby="cd-coupon-toggle" placeholder="הכניסו קוד" dir="ltr" autocomplete="off" spellcheck="false" onkeydown="if(event.key===\'Enter\'){event.preventDefault();window._cdApply()}">' +
                '<button type="button" class="cd-discount__apply" onclick="window._cdApply()">החל</button>' +
              '</div>' +
            '</div>' +
          '</div>' +
          '<p id="cd-coupon-status" role="status" aria-live="polite" style="margin:8px 0;font-size:14px"></p>' +
          '<div class="cart-drawer__secondary">' +
            '<button type="button" class="cart-drawer__continue" data-cart-close>המשיכו לקנות</button>' +
            '<button type="button" class="cart-drawer__clear-all" data-action="clear-cart">רוקן את הסל</button>' +
          '</div>' +
        '</div>'
      );
    }

    /* ---------- Render ---------- */
    /* ---------- compare_at_price enrichment ----------
       /cart.js line items do NOT include compare_at_price (documented AJAX
       limitation), so buildPriceHtml/tplBundle never see a sale price. We
       fetch it from /products/{handle}.js (variant.compare_at_price, in cents)
       and cache it. applyCompareCache() runs synchronously on every render so
       cached items show the strikethrough instantly; ensureCompareData()
       lazily fills gaps then triggers one more render. */
    var COMPARE_CACHE = {};   // variantId -> compareCents (>price) or 0
    var COMPARE_PENDING = {}; // handle -> in-flight Promise (dedupe)
    try {
      var _cc = sessionStorage.getItem('cd_compare_cache');
      if (_cc) COMPARE_CACHE = JSON.parse(_cc) || {};
    } catch (e) {}

    function applyCompareCache(cart) {
      if (!cart || !cart.items) return;
      cart.items.forEach(function(it) {
        var c = COMPARE_CACHE[it.variant_id];
        if (c && c > it.price) it.compare_at_price = c;
      });
    }

    function ensureCompareData(cart) {
      if (!cart || !cart.items) return;
      var handles = {};
      cart.items.forEach(function(it) {
        if (it.properties && it.properties['_free_gift']) return;
        if (Object.prototype.hasOwnProperty.call(COMPARE_CACHE, it.variant_id)) return;
        if (it.handle) handles[it.handle] = true;
      });
      var list = Object.keys(handles);
      if (!list.length) return;
      Promise.all(list.map(function(h) {
        if (COMPARE_PENDING[h]) return COMPARE_PENDING[h];
        var pr = fetch('/products/' + h + '.js', { headers: { 'Accept': 'application/json' } })
          .then(function(r) { return r.ok ? r.json() : null; })
          .then(function(prod) {
            if (prod && prod.variants) {
              prod.variants.forEach(function(v) {
                COMPARE_CACHE[v.id] = (v.compare_at_price && v.compare_at_price > v.price) ? v.compare_at_price : 0;
              });
            }
          })
          .catch(function() {})
          .then(function() { delete COMPARE_PENDING[h]; });
        COMPARE_PENDING[h] = pr;
        return pr;
      })).then(function() {
        // Sentinel: any cart variant still uncached after its fetch resolved
        // (deleted/unpublished product, or variant removed) gets 0 so the
        // render -> ensureCompareData -> fetch cycle converges instead of looping.
        (cart.items || []).forEach(function(it) {
          if (it.properties && it.properties['_free_gift']) return;
          if (!Object.prototype.hasOwnProperty.call(COMPARE_CACHE, it.variant_id)) {
            COMPARE_CACHE[it.variant_id] = 0;
          }
        });
        try { sessionStorage.setItem('cd_compare_cache', JSON.stringify(COMPARE_CACHE)); } catch (e) {}
        applyCompareCache(STATE.cart);
        render();
      });
    }

    function render() {
      var body      = document.getElementById('cart-drawer-body');
      var footer    = document.getElementById('cart-drawer-footer');
      var shipZone  = document.getElementById('cart-ship-zone');
      var promoZone = document.getElementById('cart-promo-zone');
      var countEl   = document.getElementById('cart-drawer-count');
      var headerCount = document.querySelector('.cart-count');

      if (!body || !footer) return;

      var cart = STATE.cart;
      if (!cart) {
        body.innerHTML = tplSkeleton();
        footer.innerHTML = '';
        return;
      }

      // Price rows use Shopify original/final line amounts, as checkout does.

      pruneUserQty();
      var grouped = groupItems(cart.items);

      // Count only non-addon non-gift items. Use displayQtyFor() so cart-transform
      // bundles whose server quantity is clamped to 1 still contribute the user's
      // intended count to the header badge.
      // Optimistic add-in-flight: CartAPI.add bumps this BEFORE the network
      // round-trip for simple promo-eligible items, so the promo/ship bars and
      // header badge react instantly. Cleared when the server cart lands.
      var optimistic = STATE.optimisticAddCount || 0;

      var realCount = cart.items.reduce(function(sum, it) {
        var role   = it.properties && it.properties._bundle_role;
        var isGift = it.properties && it.properties['_free_gift'];
        if (role === 'addon' || isGift) return sum;
        return sum + displayQtyFor(it);
      }, 0) + optimistic;

      // 3+1 promo count EXCLUDES Mystery Box lines: the tiered Mystery Box
      // discount must not combine with the 3+1 promo (business rule). Box lines
      // still count in realCount above (header/title/total), just not the promo.
      var promoCount = cart.items.reduce(function(sum, it) {
        if (!isPromoEligible(it)) return sum;
        return sum + displayQtyFor(it);
      }, 0) + optimistic;

      // Header badge and drawer title use realCount (excludes addons + free gifts)
      if (headerCount) {
        headerCount.textContent = realCount;
        headerCount.style.display = realCount > 0 ? '' : 'none';
      }
      if (countEl) {
        countEl.textContent = '| ' + realCount + ' ' + (realCount === 1 ? 'פריט' : 'פריטים');
      }

      var promoFiredNow = cdPromoFired(promoCount, cart);
      var promoStage    = cdPromoStage(promoCount, cart);

      // Broadcast cart state to out-of-drawer consumers (snippets/cart-progress-chip.liquid).
      // Fired here, BEFORE the empty-cart early return below, so an emptied cart
      // is reported too. Counts are passed out rather than recomputed by the
      // consumer: isPromoEligible/displayQtyFor live in this file only.
      //
      // The state is ALSO parked on window.JRS_CART_STATE, because this file runs
      // on DOMContentLoaded while consumers gated behind JRSDefer only wake on the
      // first user interaction - a shopper who reads for two seconds before
      // scrolling would otherwise attach a listener after the only dispatch of the
      // page and see nothing. Late consumers read the global; live ones use the event.
      try {
        var jrsCartState = {
          realCount: realCount,
          promoCount: promoCount,
          promoFired: promoFiredNow,
          promoStage: promoStage,
          shipTotal: shipEligibleTotal(cart)
        };
        window.JRS_CART_STATE = jrsCartState;
        document.dispatchEvent(new CustomEvent('jrs:cart:render', { detail: jrsCartState }));
      } catch (e) {}

      // Stage 3 -> gift picker takes over the rail strip (renderUpsellRail)
      STATE.giftPickerVisible = !!(PROMO_ENABLED && !promoFiredNow &&
        promoCount === PROMO_THRESHOLD && !cart.total_discount && cdPromoDiscountCents(cart) === 0 &&
        optimistic === 0);

      if (promoZone) {
        var prevStage = STATE.prevPromoStage;
        promoZone.innerHTML = tplIncentiveBar(cart, promoCount, promoFiredNow);

        // Confetti ONLY when the discount really fires (server-confirmed
        // stage transition into "fired") - not at the 3-item threshold, and
        // never on a first render of an already-fired cart.
        if (promoStage === PROMO_THRESHOLD + 1 &&
            typeof prevStage === 'number' && prevStage < PROMO_THRESHOLD + 1) {
          triggerConfetti(promoZone);
        }

        STATE.prevPromoCount = promoCount;
        STATE.prevPromoStage = promoStage;
      }
      // The old separate shipping bar is merged into the incentive bar; the
      // zone stays in the DOM for layout compatibility but renders nothing.
      if (shipZone) shipZone.innerHTML = '';

      // Funnel: one JRS_PromoStageView per stage per drawer-open (dedup set
      // reset in CartAPI.open). dataLayer + Clarity only - no pixel spend.
      cdTrackPromoStage(promoStage);

      if (realCount === 0) {
        body.innerHTML = tplEmpty();
        if (shipZone) shipZone.innerHTML = '';
        footer.innerHTML = '';
        var railEl = document.getElementById('cart-drawer-rail');
        if (railEl) railEl.innerHTML = '';
        return;
      }

      // Includes line and order discounts, exactly as calculated by Shopify.
      var displayTotal = cart.total_price;

      var html = '';
      for (var gi = 0; gi < grouped.ordered.length; gi++) {
        var grp = grouped.ordered[gi];
        if (grp.type === 'bundle') html += tplBundle(grouped.bundles[grp.id]);
        else if (grp.type === 'box') html += tplBox(grouped.boxes[grp.id]);
        else html += tplItem(grp.data);
      }
      // Add-in-flight: newest lines come back first from /cart.js, so the
      // skeleton for the incoming item sits at the top.
      body.innerHTML = (optimistic > 0 ? tplSkeletonRow() : '') + html;
      var couponInput = footer.querySelector('#cd-coupon');
      var couponValue = couponInput ? couponInput.value : '';
      var couponOpen = !!footer.querySelector('.cd-discount.open');
      var couponFocused = couponInput && document.activeElement === couponInput;
      footer.innerHTML = tplFooter(cart, displayTotal, promoCount);
      if (couponOpen) {
        footer.querySelector('.cd-discount').classList.add('open');
        footer.querySelector('.cd-discount__toggle').setAttribute('aria-expanded', 'true');
      }
      var nextCoupon = footer.querySelector('#cd-coupon');
      if (nextCoupon) {
        nextCoupon.value = couponValue;
        syncCouponCheckout();
        if (couponFocused) nextCoupon.focus({ preventScroll: true });
      }
      renderUpsellRail();
    }

    // External signal: cart-upsell-popup.js promoted complement products
    document.addEventListener('cart:upsells-updated', function() {
      renderUpsellRail();
    });

    /* ---------- Event delegation (single handler for all cart UI) ---------- */
    document.addEventListener('click', function(e) {
      // Close button & continue shopping
      var closeBtn = e.target.closest('#cart-close, [data-cart-close]');
      if (closeBtn) {
        e.preventDefault();
        CartAPI.close();
        return;
      }

      // Bundle addons toggle (expand/collapse)
      var bundleToggle = e.target.closest('[data-bundle-toggle]');
      if (bundleToggle) {
        var isOpen = bundleToggle.getAttribute('aria-expanded') === 'true';
        bundleToggle.setAttribute('aria-expanded', isOpen ? 'false' : 'true');
        var addonsEl = bundleToggle.nextElementSibling;
        if (addonsEl) addonsEl.hidden = isOpen;
        return;
      }

      // Cart toggle
      var toggle = e.target.closest('[data-cart-toggle]');
      if (toggle) {
        e.preventDefault();
        CartAPI.open(toggle);
        return;
      }

      // Overlay
      if (e.target.id === 'cart-overlay') {
        CartAPI.close();
        return;
      }

      // Item quantity change
      var changeBtn = e.target.closest('[data-action="change"][data-key]');
      if (changeBtn) {
        if (changeBtn.disabled) return;
        changeBtn.disabled = true;
        // Lock drawer immediately so layout shifts don't cause mis-clicks
        var drawerEl = document.getElementById('cart-drawer');
        if (drawerEl) drawerEl.classList.add('cart-drawer--loading');
        var key = changeBtn.dataset.key;
        var newQty = parseInt(changeBtn.dataset.qty, 10);
        if (isNaN(newQty) || newQty < 0) { changeBtn.disabled = false; return; }
        CartAPI.changeByKey(key, newQty);
        return;
      }

      // Upsell / gift-picker quick-add - inline spinner, no full drawer redraw
      var upsellBtn = e.target.closest('[data-action="upsell-add"]');
      if (upsellBtn) {
        e.preventDefault();
        if (upsellBtn.disabled) return;
        var variantId = parseInt(upsellBtn.dataset.variantId, 10);
        if (!variantId) return;
        var upsellPid = parseInt(upsellBtn.dataset.productId, 10) || upsellBtn.dataset.productId;
        var upsellFrom = upsellBtn.dataset.from || 'drawer_rail';
        upsellBtn.disabled = true;
        upsellBtn.setAttribute('aria-busy', 'true');
        CartAPI.add({ id: variantId, quantity: 1 }).then(function() {
          // Attach-funnel event, fired once on a successful quick-add.
          // Guarded (typeof fbq + try/catch) so a blocked/absent pixel in
          // in-app webviews never throws. Mirrors JRS_DrawerOpen/JRS_CheckoutClick.
          try {
            if (typeof window.fbq === 'function') {
              window.fbq('trackCustom', 'JRS_UpsellAdd', { pid: upsellPid, from: upsellFrom });
            }
          } catch (err) {}
          if (upsellFrom === 'gift_picker') cdTrack('JRS_GiftPickerAdd', { pid: upsellPid }, true);
          // Drawer is already open; rail re-renders on next render() and
          // filters out the just-added product. No CartAPI.open() needed.
        }).catch(function() {
          upsellBtn.disabled = false;
          upsellBtn.setAttribute('aria-busy', 'false');
        });
        return;
      }

      // Bundle change
      var clearBtn = e.target.closest('[data-action="clear-cart"]');
      if (clearBtn) {
        if (clearBtn.disabled) return;
        if (!window.confirm('לרוקן את כל הסל?')) return;
        clearBtn.disabled = true;
        setBusy(true);
        // /cart/clear.js needs no line keys, so it cannot hit the stale-key
        // failure mode - it always empties the cart.
        request('/cart/clear.js', { method: 'POST' }).then(function(cart) {
          STATE.cart = cart;
          STATE.userQty = {};
          render();
          setBusy(false);
        }).catch(function(e2) {
          setBusy(false);
          clearBtn.disabled = false;
          showError(e2.message || 'לא ניתן לרוקן את הסל');
        });
        return;
      }

      var bundleBtn = e.target.closest('[data-action="bundle-change"][data-bundle-keys]');
      if (bundleBtn) {
        if (bundleBtn.disabled) return;
        bundleBtn.disabled = true;
        var drawerEl2 = document.getElementById('cart-drawer');
        if (drawerEl2) drawerEl2.classList.add('cart-drawer--loading');
        var keys = bundleBtn.dataset.bundleKeys.split('|');
        var bqty = parseInt(bundleBtn.dataset.qty, 10);
        if (isNaN(bqty) || bqty < 0) { bundleBtn.disabled = false; return; }
        CartAPI.changeBundleByKeys(keys, bqty);
        return;
      }
    });

    // Release scroll lock on navigation (user clicks a link while drawer is open)
    window.addEventListener('beforeunload', function() {
      document.body.style.overflow = '';
      document.body.style.touchAction = '';
    });

    // Escape closes drawer; Enter confirms qty input; Tab stays inside.
    // 6.8.26: the drawer declares role="dialog" aria-modal="true" but 13 of 14 Tab
    // presses landed on the page behind it, which contradicts its own semantics and
    // lets a keyboard user edit the page under an open modal. Escape, the scroll lock
    // and focus restoration (STATE.lastFocused) were already correct - only the trap
    // was missing.
    document.addEventListener('keydown', function(e) {
      var drawer = document.getElementById('cart-drawer');
      var isOpen = drawer && drawer.classList.contains('active');

      if (e.key === 'Escape') {
        if (isOpen) {
          e.preventDefault();
          CartAPI.close();
        }
        return;
      }

      if (e.key !== 'Tab' || !isOpen) return;

      var FOCUSABLE = 'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';
      var f = Array.prototype.filter.call(drawer.querySelectorAll(FOCUSABLE), function(el) {
        return el.offsetWidth || el.offsetHeight || el.getClientRects().length;
      });
      if (!f.length) return;
      var first = f[0], last = f[f.length - 1];

      if (!drawer.contains(document.activeElement)) {
        e.preventDefault();
        first.focus();
      } else if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    });

    // Intercept cart-add forms (theme-wide AJAX add to cart)
    document.addEventListener('submit', function(e) {
      var form = e.target;
      if (!form || !form.tagName || form.tagName !== 'FORM') return;
      if (!form.action || form.action.indexOf('/cart/add') < 0) return;

      e.preventDefault();
      var submitBtn = form.querySelector('[type="submit"]');
      if (submitBtn) submitBtn.disabled = true;

      var fd = new FormData(form);
      // Convert FormData to JSON for /cart/add.js
      var item = { id: parseInt(fd.get('id'), 10), quantity: parseInt(fd.get('quantity') || '1', 10), properties: {} };
      fd.forEach(function(val, key) {
        if (key.indexOf('properties[') === 0 && val) {
          item.properties[key.replace('properties[', '').replace(']', '')] = val;
        }
      });

      CartAPI.add(item).then(function() {
        if (submitBtn) submitBtn.disabled = false;
        CartAPI.open();
      }).catch(function() {
        if (submitBtn) submitBtn.disabled = false;
      });
    });

    /* Only Shopify-confirmed cart discounts carry into checkout. Typing a code
       does not apply it or change the checkout destination. */
    var couponPending = false;

    function syncCouponCheckout() {
      var btn = document.getElementById('cart-checkout-btn');
      if (btn) btn.setAttribute('href', '/checkout?locale=he');
      var apply = document.querySelector('.cd-discount__apply');
      if (apply) {
        apply.disabled = couponPending;
        apply.setAttribute('aria-busy', String(couponPending));
      }
    }

    /* ---------- Checkout gate: minimum order ---------- */
    document.addEventListener('click', function(e) {
      var btn = e.target.closest('#cart-checkout-btn');
      if (!btn) return;
      if (couponPending) { e.preventDefault(); return; }
      var cart = CartAPI.get();
      if (cart) {
        var gateTotal = cart.items.reduce(function(s, it) {
          return (it.properties && it.properties['_free_gift']) ? s : s + it.final_line_price;
        }, 0);
        if (gateTotal < MIN_ORDER) {
          e.preventDefault();
          showError('מינימום הזמנה הוא ' + fmt(MIN_ORDER) + ' - הוסיפו עוד פריטים לעגלה');
          cdTrack('JRS_MinOrderBlock', { value: Math.round(gateTotal) / 100 }, true);
          return;
        }
      }
      syncCouponCheckout();
      // Gate passed (or no cart snapshot yet); the click proceeds to checkout.
      // Fire the funnel event and tag the cart's entry point.
      var snap = cdCartSnapshot();
      if (typeof window.fbq === 'function') {
        try { window.fbq('trackCustom', 'JRS_CheckoutClick', { value: snap.value, entry: 'drawer' }); } catch (err) {}
      }
      cdSetCheckoutEntry('drawer');
    }, true);


    /* ---------- Initial sync ---------- */
    function init() {
      CartAPI.fetch();
      // Hide initial header count of 0
      var hc = document.querySelector('.cart-count');
      if (hc && hc.textContent.trim() === '0') hc.style.display = 'none';

      // Hide Enable ESC button now and watch for dynamic injection
      hideEnableEsc();
      try {
        // hideEnableEsc() scans every element in the document and reads
        // textContent on every leaf. Unthrottled on a subtree observer it ran on
        // each of the three renders per cart change plus every app/carousel/lazy
        // mutation on the page. rAF-coalesced now: same effect, one scan per frame.
        var _escT = 0;
        new MutationObserver(function() {
          if (_escT) return;
          _escT = requestAnimationFrame(function() { _escT = 0; hideEnableEsc(); });
        }).observe(document.body, { childList: true, subtree: true });
      } catch(e) {}
    }

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', init);
    } else {
      init();
    }

    window._cdApply = function() {
      var el = document.getElementById('cd-coupon');
      if (!el || couponPending) return;
      var code = el.value.trim();
      if (!code) { el.focus(); return; }
      function report(message) {
        var status = document.getElementById('cd-coupon-status');
        if (status) status.textContent = message;
      }
      couponPending = true;
      syncCouponCheckout();
      report('בודקים את הקוד...');
      return enqueue(function() {
        // Preserve other server-known codes. Shopify determines eligibility and
        // combinations; never calculate a coupon reduction in the browser.
        var codes = ((STATE.cart && STATE.cart.discount_codes) || []).filter(function(d) { return d.applicable; }).map(function(d) { return d.code; });
        if (!codes.some(function(c) { return c.toLowerCase() === code.toLowerCase(); })) codes.push(code);
        var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
        return request(root + 'cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ discount: codes.join(',') })
        }).then(function(cart) {
          STATE.cart = cart;
          STATE.lastFetch = Date.now();
          pruneUserQty();
          render();
          var applied = (cart.discount_codes || []).some(function(d) {
            return d.code.toLowerCase() === code.toLowerCase() && d.applicable === true;
          });
          // Older AJAX responses expose applied code titles in allocations.
          var applications = (cart.cart_level_discount_applications || []).slice();
          (cart.items || []).forEach(function(item) {
            (item.line_level_discount_allocations || []).forEach(function(a) { applications.push(a.discount_application || {}); });
          });
          applied = applied || applications.some(function(d) {
            return d.type === 'discount_code' && String(d.title).toLowerCase() === code.toLowerCase();
          });
          report(applied ? 'הקוד הוחל. הסכום בעגלה עודכן.' : 'הקוד לא הוחל על העגלה. כדאי לבדוק את הקוד ואת תנאי ההטבה.');
          return cart;
        }).catch(function() {
          report('לא הצלחנו לבדוק את הקוד. אפשר לנסות שוב.');
          return null;
        }).then(function(cart) {
          couponPending = false;
          syncCouponCheckout();
          return cart;
        });
      });
    };
  })();

